import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import useHub from "data/hub/useHub";

import { useFeatures } from "components/FeatureFlag";
import { useScreenManager } from "components/ScreenManager";

import { SCREEN_TYPE } from "../../Container";
import { useWorkDesk } from "./useWorkDesk";
import { useWorkDeskEnabled } from "./useWorkDeskEnabled";

// the work desk demotes this long after an exit begins — longer than the
// 160ms fade-out so the screen has faded before it leaves the z-stack.
// Exported so the exit-confirmation guard can wait out the same exit.
export const WORK_DESK_EXIT_MS = 180;

/*
 * Keeps the route and the Work Desk screen in sync:
 * - entering a /workdesk (or new-client-card) path promotes the screen
 * - a toolbar control promoting another screen over it exits the route
 * - leaving the route (browser back, iframe sync) restores the prior screen
 * The screen is unregistered when the flags turn off mid-session so
 * front() never reports a screen that no longer renders.
 */
export function useWorkDeskScreen() {
  const workDeskEnabled = useWorkDeskEnabled();
  const ffScreenManager = useFeatures("work-desk-screen-manager");
  const hub = useHub();
  const hubId = hub?.hubId;
  const { front, promote, isRegistered, unregister } = useScreenManager();
  const history = useHistory();
  const location = useLocation();
  const {
    shouldShowWorkDesk,
    exitWorkDesk,
    isClientCardDestination,
    isWorkdeskClientPath,
  } = useWorkDesk();

  const enabled = Boolean(workDeskEnabled && ffScreenManager && hubId);
  const frontScreen = front();
  const registered = isRegistered(SCREEN_TYPE.WORK_DESK);

  const prevFrontRef = useRef();
  const lastOtherFrontRef = useRef();
  // set when the hook's own deferred demote promotes another screen, so
  // the next pass does not misread it as a user-driven toolbar promote
  const selfPromotedRef = useRef(false);
  // route-driven exits fade the screen out (160ms) before the demote so
  // leaving is choreographed like entering; the demote lands just after
  const [leaving, setLeaving] = useState(false);
  const leaveTimerRef = useRef();

  // last work-desk URL, restored when a client card closes so the desk
  // resumes its exact view. warmClientCardRef is set only on the legacy
  // warm-keep path below, so the new client card is unaffected.
  const workDeskReturnRef = useRef();
  const warmClientCardRef = useRef(false);

  useEffect(() => {
    if (enabled && shouldShowWorkDesk && isWorkdeskClientPath) {
      workDeskReturnRef.current = location.pathname + location.search;
    }
  }, [
    enabled,
    shouldShowWorkDesk,
    isWorkdeskClientPath,
    location.pathname,
    location.search,
  ]);

  // commands for the embedded work-desk app: exiting resets the session,
  // hover/entry intent prefetches the next one (each id posts once)
  const [workSessionCommand, setWorkSessionCommand] = useState(null);
  const commandIdRef = useRef(0);
  const sendWorkSessionCommand = useCallback((command) => {
    commandIdRef.current += 1;
    setWorkSessionCommand({ id: commandIdRef.current, command: command });
  }, []);
  const prefetchWorkSession = useCallback(() => {
    sendWorkSessionCommand("prefetch");
  }, [sendWorkSessionCommand]);

  useEffect(() => {
    return () => {
      clearTimeout(leaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (registered) unregister(SCREEN_TYPE.WORK_DESK);
      // drop transient state so a mid-session re-enable starts clean
      // instead of misreading the stale prevFront as a toolbar exit
      clearTimeout(leaveTimerRef.current);
      if (leaving) setLeaving(false);
      prevFrontRef.current = undefined;
      lastOtherFrontRef.current = undefined;
      selfPromotedRef.current = false;
      warmClientCardRef.current = false;
      workDeskReturnRef.current = undefined;
      return;
    }
    if (!registered) return;

    const prevFront = prevFrontRef.current;
    prevFrontRef.current = frontScreen;
    const selfPromoted = selfPromotedRef.current;
    selfPromotedRef.current = false;
    const wasWarmClientCard = warmClientCardRef.current;
    warmClientCardRef.current = false;
    if (frontScreen !== SCREEN_TYPE.WORK_DESK) {
      lastOtherFrontRef.current = frontScreen;
    }

    if (shouldShowWorkDesk && leaving) {
      clearTimeout(leaveTimerRef.current);
      setLeaving(false);
      return;
    }

    if (shouldShowWorkDesk && frontScreen !== SCREEN_TYPE.WORK_DESK) {
      if (prevFront === SCREEN_TYPE.WORK_DESK && !selfPromoted) {
        // a promote-over exit ends the session just like a route exit
        sendWorkSessionCommand("reset");
        exitWorkDesk();
      } else {
        promote(SCREEN_TYPE.WORK_DESK);
      }
    } else if (!shouldShowWorkDesk && frontScreen === SCREEN_TYPE.WORK_DESK) {
      if (isClientCardDestination) {
        warmClientCardRef.current = true;
        return;
      }
      const atHubRoot = location.pathname === `/hub/${hubId}`;
      if (wasWarmClientCard && atHubRoot && workDeskReturnRef.current) {
        const returnUrl = workDeskReturnRef.current;
        workDeskReturnRef.current = undefined;
        history.replace(returnUrl);
        return;
      }
      if (!leaving) {
        setLeaving(true);
        // the session ends with the visit; the embedded app clears it and
        // waits for the next intent before creating a fresh one
        sendWorkSessionCommand("reset");
        leaveTimerRef.current = setTimeout(() => {
          selfPromotedRef.current = true;
          promote(lastOtherFrontRef.current || SCREEN_TYPE.WORKFLOW);
          setLeaving(false);
        }, WORK_DESK_EXIT_MS);
      }
    }
  }, [
    enabled,
    registered,
    shouldShowWorkDesk,
    isClientCardDestination,
    frontScreen,
    leaving,
    promote,
    unregister,
    exitWorkDesk,
    sendWorkSessionCommand,
    history,
    hubId,
    location.pathname,
  ]);

  return {
    showWorkDeskScreen: enabled,
    isWorkDeskScreenInFront: frontScreen === SCREEN_TYPE.WORK_DESK && !leaving,
    workSessionCommand: workSessionCommand,
    prefetchWorkSession: prefetchWorkSession,
  };
}

export default useWorkDeskScreen;
